import React, { useRef, useState, useLayoutEffect } from 'react';

export const A4 = { w: 794, h: 1123 };

export const CV_TEMPLATES = [
  { key: 'executive', name: 'تنفيذي', desc: 'ترويسة ملوّنة عريضة ومحتوى بعمود واحد — رسمي ومباشر' },
  { key: 'sidebar', name: 'عمودان', desc: 'شريط جانبي داكن للمعلومات والمهارات مع مسار مهني واضح' },
  { key: 'minimal', name: 'أنيق بسيط', desc: 'تايبوغرافي هادئ بخطوط رفيعة — مناسب للطباعة بالأبيض والأسود' },
];

export const ACCENTS = [
  { key: 'teal', name: 'أخضر تسيار', c: '#0d9488', d: '#0f3b36' },
  { key: 'navy', name: 'كحلي', c: '#1e3a8a', d: '#16233b' },
  { key: 'bordeaux', name: 'عنابي', c: '#9f1239', d: '#3f0d1c' },
  { key: 'graphite', name: 'رمادي فاخر', c: '#475569', d: '#1e293b' },
  { key: 'gold', name: 'ذهبي', c: '#a16207', d: '#2e2208' },
];

const COACH_TYPES = { head_coach: 'مدرب رئيسي', assistant_coach: 'مدرب مساعد', goalkeeper_coach: 'مدرب حراس مرمى', fitness_coach: 'مدرب لياقة بدنية', conditioning_coach: 'مدرب إعداد بدني', performance_analyst: 'محلل أداء فني', youth_coach: 'مدرب فئات سنية', other: 'مدرب رياضي' };
const EMPLOYMENT = { full_time: 'دوام كامل', part_time: 'دوام جزئي', seasonal: 'موسمي', freelance: 'متعاون' };

const mon = (d) => (d ? new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { year: 'numeric', month: 'short' }).format(new Date(d)) : '');
const range = (a, b) => `${mon(a)} — ${b ? mon(b) : 'حتى الآن'}`;
const specialtyOf = (p) => (p.coach_type === 'other' ? (p.custom_specialty || 'مدرب رياضي') : (COACH_TYPES[p.coach_type] || 'مدرب رياضي'));
const FONT_H = 'Cairo, sans-serif';
const FONT_B = 'Tajawal, Cairo, sans-serif';

const photoOf = (p) => (p.photo_file_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.photo_file_id}` : null);

const Chip = ({ children, bg, color, br = 6 }) => (
  <span style={{ background: bg, color, borderRadius: br, padding: '3px 9px', fontSize: 12.3, fontWeight: 600, display: 'inline-block' }}>{children}</span>
);

/* ——— قالب 1: تنفيذي ——— */
const Executive = ({ p, exps, certs, acc, opt }) => {
  const photo = photoOf(p);
  const H = ({ t }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0 9px' }}>
      <span style={{ width: 4, height: 15, background: acc.c, borderRadius: 2 }} />
      <span style={{ fontFamily: FONT_H, fontSize: 15.2, fontWeight: 800, color: acc.d, letterSpacing: 0 }}>{t}</span>
      <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: FONT_B, color: '#111827' }}>
      <div style={{ background: acc.d, padding: '26px 34px', display: 'flex', alignItems: 'center', gap: 20, position: 'relative', overflow: 'hidden' }}>
        <span style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${acc.c}55, transparent 60%)` }} />
        {photo
          ? <img src={photo} alt="" style={{ width: 92, height: 92, borderRadius: 14, objectFit: 'cover', border: `3px solid ${acc.c}`, position: 'relative' }} />
          : <div style={{ width: 92, height: 92, borderRadius: 14, background: '#ffffff22', border: `3px solid ${acc.c}`, position: 'relative' }} />}
        <div style={{ position: 'relative', flex: 1 }}>
          <div style={{ fontFamily: FONT_H, fontSize: 30.4, fontWeight: 900, color: '#ffffff', lineHeight: 1.45 }}>{p.full_name_ar || '—'}</div>
          {p.full_name_en && <div dir="ltr" style={{ fontSize: 12.3, color: '#ffffff99', textAlign: 'right', marginTop: 7 }}>{p.full_name_en}</div>}
          <div style={{ fontSize: 15.2, fontWeight: 700, color: acc.c === '#475569' ? '#cbd5e1' : '#ffffff', marginTop: 5, opacity: 0.95 }}>{specialtyOf(p)}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 10 }}>
            {p.years_experience != null && <Chip bg="#ffffff1f" color="#fff">{p.years_experience} سنوات خبرة</Chip>}
            {p.employment_type && <Chip bg="#ffffff1f" color="#fff">{EMPLOYMENT[p.employment_type]}</Chip>}
            {(p.city || p.region) && <Chip bg="#ffffff1f" color="#fff">{[p.city, p.region].filter(Boolean).join(' · ')}</Chip>}
            {p.nationality && <Chip bg="#ffffff1f" color="#fff">{p.nationality}</Chip>}
          </div>
        </div>
        {opt.contact && (
          <div style={{ position: 'relative', textAlign: 'left', fontSize: 12.3, color: '#ffffffcc', lineHeight: 2 }}>
            {p.phone && <div dir="ltr">{p.phone}</div>}
            {p.email && <div dir="ltr">{p.email}</div>}
          </div>
        )}
      </div>

      <div style={{ padding: '18px 34px 26px' }}>
        {p.bio && <><H t="نبذة مهنية" /><p style={{ fontSize: 13.5, lineHeight: 1.95, color: '#374151', margin: 0 }}>{p.bio}</p></>}

        {exps.length > 0 && <><H t="الخبرات المهنية" />
          {exps.map((e) => (
            <div key={e.id} style={{ marginBottom: 11, paddingInlineStart: 12, borderInlineStart: `2px solid #e9eef4` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontFamily: FONT_H, fontSize: 14.6, fontWeight: 800, color: '#0f172a' }}>{e.title}</span>
                <span style={{ fontSize: 11.7, color: '#94a3b8', whiteSpace: 'nowrap' }}>{range(e.start_date, e.end_date)}</span>
              </div>
              <div style={{ fontSize: 12.9, color: acc.c, fontWeight: 700, marginTop: 1 }}>{e.organization_name}{e.category ? ` · ${e.category}` : ''}</div>
              {e.description && <p style={{ fontSize: 12.6, color: '#4b5563', lineHeight: 1.8, margin: '3px 0 0' }}>{e.description}</p>}
            </div>
          ))}</>}

        {certs.length > 0 && <><H t="الشهادات والرخص" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px 16px' }}>
            {certs.map((c) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, background: '#f8fafc', borderRadius: 8, padding: '7px 10px' }}>
                <span style={{ fontSize: 12.9, fontWeight: 700, color: '#1f2937' }}>{c.name}{c.level ? ` · ${c.level}` : ''}</span>
                <span style={{ fontSize: 11.1, color: '#94a3b8', whiteSpace: 'nowrap' }}>{c.issuing_authority || mon(c.issue_date)}</span>
              </div>
            ))}
          </div></>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            {(p.skills || []).length > 0 && <><H t="المهارات" />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>{p.skills.map((s, i) => <Chip key={i} bg={`${acc.c}14`} color={acc.d}>{s}</Chip>)}</div></>}
          </div>
          <div>
            {(p.education || []).length > 0 && <><H t="المؤهلات" />
              <div style={{ fontSize: 12.9, color: '#374151', lineHeight: 1.9 }}>{p.education.map((e, i) => <div key={i}>• {e}</div>)}</div></>}
          </div>
        </div>

        {(p.languages || []).length > 0 && <><H t="اللغات" /><div style={{ fontSize: 13.5, color: '#374151' }}>{p.languages.join('  ·  ')}</div></>}
      </div>
    </div>
  );
};

/* ——— قالب 2: عمودان بشريط جانبي ——— */
const Sidebar = ({ p, exps, certs, acc, opt }) => {
  const photo = photoOf(p);
  const SH = ({ t }) => <div style={{ fontFamily: FONT_H, fontSize: 12.9, fontWeight: 800, color: '#ffffff', opacity: 0.6, margin: '18px 0 7px' }}>{t}</div>;
  const MH = ({ t }) => (
    <div style={{ margin: '18px 0 10px' }}>
      <span style={{ fontFamily: FONT_H, fontSize: 15.8, fontWeight: 800, color: acc.d }}>{t}</span>
      <div style={{ height: 3, width: 38, background: acc.c, borderRadius: 2, marginTop: 9 }} />
    </div>
  );
  return (
    <div style={{ fontFamily: FONT_B, display: 'flex', minHeight: A4.h, color: '#111827' }}>
      <aside style={{ width: 252, background: acc.d, color: '#fff', padding: '30px 22px', flexShrink: 0 }}>
        <div style={{ textAlign: 'center' }}>
          {photo
            ? <img src={photo} alt="" style={{ width: 112, height: 112, borderRadius: '50%', objectFit: 'cover', border: `4px solid ${acc.c}` }} />
            : <div style={{ width: 112, height: 112, borderRadius: '50%', background: '#ffffff1a', border: `4px solid ${acc.c}`, margin: '0 auto' }} />}
          <div style={{ fontFamily: FONT_H, fontSize: 21.1, fontWeight: 900, marginTop: 12, lineHeight: 1.35 }}>{p.full_name_ar || '—'}</div>
          <div style={{ fontSize: 13.5, color: acc.c === '#475569' ? '#cbd5e1' : '#fff', opacity: 0.85, marginTop: 3 }}>{specialtyOf(p)}</div>
        </div>
        <div style={{ height: 1, background: '#ffffff26', margin: '18px 0 0' }} />

        {opt.contact && (p.phone || p.email) && <>
          <SH t="التواصل" />
          <div style={{ fontSize: 12.3, lineHeight: 2.1, opacity: 0.92 }}>
            {p.phone && <div dir="ltr" style={{ textAlign: 'right' }}>{p.phone}</div>}
            {p.email && <div dir="ltr" style={{ textAlign: 'right', wordBreak: 'break-all' }}>{p.email}</div>}
          </div>
        </>}

        <SH t="معلومات عامة" />
        <div style={{ fontSize: 12.6, lineHeight: 2.05, opacity: 0.92 }}>
          {p.nationality && <div>الجنسية: {p.nationality}</div>}
          {(p.city || p.region) && <div>الموقع: {[p.city, p.region].filter(Boolean).join(' · ')}</div>}
          {p.years_experience != null && <div>سنوات الخبرة: {p.years_experience}</div>}
          {p.employment_type && <div>التفرغ: {EMPLOYMENT[p.employment_type]}</div>}
          {p.willing_to_relocate && <div>قابل للانتقال بين المدن</div>}
        </div>

        {(p.skills || []).length > 0 && <><SH t="المهارات" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>{p.skills.map((s, i) => <Chip key={i} bg="#ffffff1f" color="#fff">{s}</Chip>)}</div></>}

        {(p.languages || []).length > 0 && <><SH t="اللغات" />
          <div style={{ fontSize: 12.6, lineHeight: 1.95, opacity: 0.92 }}>{p.languages.map((l, i) => <div key={i}>• {l}</div>)}</div></>}

        {(p.education || []).length > 0 && <><SH t="المؤهلات" />
          <div style={{ fontSize: 12.3, lineHeight: 1.9, opacity: 0.92 }}>{p.education.map((e, i) => <div key={i}>• {e}</div>)}</div></>}
      </aside>

      <main style={{ flex: 1, padding: '30px 28px' }}>
        {p.bio && <><MH t="نبذة مهنية" /><p style={{ fontSize: 13.5, lineHeight: 1.95, color: '#374151', margin: 0 }}>{p.bio}</p></>}

        {exps.length > 0 && <><MH t="المسيرة التدريبية" />
          <div style={{ position: 'relative', paddingInlineStart: 14 }}>
            <span style={{ position: 'absolute', insetInlineStart: 3, top: 6, bottom: 6, width: 2, background: '#e9eef4' }} />
            {exps.map((e) => (
              <div key={e.id} style={{ position: 'relative', marginBottom: 12 }}>
                <span style={{ position: 'absolute', insetInlineStart: -15, top: 5, width: 9, height: 9, borderRadius: '50%', background: e.end_date ? '#cbd5e1' : acc.c }} />
                <div style={{ fontFamily: FONT_H, fontSize: 14.6, fontWeight: 800, color: '#0f172a' }}>{e.title}</div>
                <div style={{ fontSize: 12.9, color: acc.c, fontWeight: 700, marginTop: 1 }}>{e.organization_name}</div>
                <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 1 }}>{range(e.start_date, e.end_date)}{e.category ? ` · ${e.category}` : ''}</div>
                {e.description && <p style={{ fontSize: 12.6, color: '#4b5563', lineHeight: 1.8, margin: '3px 0 0' }}>{e.description}</p>}
              </div>
            ))}
          </div></>}

        {certs.length > 0 && <><MH t="الشهادات والرخص" />
          {certs.map((c) => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, borderBottom: '1px dashed #e5e9ef', padding: '6px 0' }}>
              <span style={{ fontSize: 13.1, fontWeight: 700, color: '#1f2937' }}>{c.name}{c.level ? ` · ${c.level}` : ''}</span>
              <span style={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{[c.issuing_authority, mon(c.issue_date)].filter(Boolean).join(' · ')}</span>
            </div>
          ))}</>}
      </main>
    </div>
  );
};

/* ——— قالب 3: أنيق بسيط ——— */
const Minimal = ({ p, exps, certs, acc, opt }) => {
  const photo = photoOf(p);
  const H = ({ t }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 10px' }}>
      <span style={{ fontFamily: FONT_H, fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>{t}</span>
      <span style={{ flex: 1, height: 1, background: '#d8dee6' }} />
    </div>
  );
  return (
    <div style={{ fontFamily: FONT_B, color: '#1f2937', padding: '42px 46px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, paddingBottom: 16, borderBottom: '2px solid #0f172a' }}>
        <div>
          <div style={{ fontFamily: FONT_H, fontSize: 32.8, fontWeight: 900, color: '#0f172a', lineHeight: 1.45 }}>{p.full_name_ar || '—'}</div>
          <div style={{ fontSize: 14.6, color: acc.c, fontWeight: 700, marginTop: 9 }}>{specialtyOf(p)}</div>
          <div style={{ fontSize: 12.3, color: '#6b7280', marginTop: 7, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {(p.city || p.region) && <span>{[p.city, p.region].filter(Boolean).join(' · ')}</span>}
            {p.nationality && <span>{p.nationality}</span>}
            {p.years_experience != null && <span>{p.years_experience} سنوات خبرة</span>}
            {opt.contact && p.phone && <span dir="ltr">{p.phone}</span>}
            {opt.contact && p.email && <span dir="ltr">{p.email}</span>}
          </div>
        </div>
        {photo && <img src={photo} alt="" style={{ width: 86, height: 86, borderRadius: 8, objectFit: 'cover', filter: 'grayscale(12%)' }} />}
      </div>

      {p.bio && <p style={{ fontSize: 13.5, lineHeight: 2, color: '#374151', margin: '16px 0 0' }}>{p.bio}</p>}

      {exps.length > 0 && <><H t="الخبرات المهنية" />
        {exps.map((e) => (
          <div key={e.id} style={{ display: 'flex', gap: 14, marginBottom: 11 }}>
            <div style={{ width: 96, flexShrink: 0, fontSize: 11.5, color: '#9ca3af', lineHeight: 1.6, paddingTop: 2 }}>{range(e.start_date, e.end_date)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONT_H, fontSize: 14.3, fontWeight: 800, color: '#0f172a' }}>{e.title} <span style={{ fontWeight: 500, color: '#6b7280' }}>— {e.organization_name}</span></div>
              {e.description && <p style={{ fontSize: 12.6, color: '#4b5563', lineHeight: 1.8, margin: '2px 0 0' }}>{e.description}</p>}
            </div>
          </div>
        ))}</>}

      {certs.length > 0 && <><H t="الشهادات والرخص" />
        {certs.map((c) => (
          <div key={c.id} style={{ display: 'flex', gap: 14, marginBottom: 5 }}>
            <div style={{ width: 96, flexShrink: 0, fontSize: 11.5, color: '#9ca3af', paddingTop: 2 }}>{mon(c.issue_date) || '—'}</div>
            <div style={{ flex: 1, fontSize: 13.1, color: '#1f2937' }}><strong style={{ fontWeight: 700 }}>{c.name}</strong>{c.level ? ` · ${c.level}` : ''}{c.issuing_authority ? ` — ${c.issuing_authority}` : ''}</div>
          </div>
        ))}</>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 18 }}>
        {(p.skills || []).length > 0 && <div><H t="المهارات" /><div style={{ fontSize: 12.6, lineHeight: 1.95, color: '#374151' }}>{p.skills.map((s, i) => <div key={i}>— {s}</div>)}</div></div>}
        {(p.education || []).length > 0 && <div><H t="المؤهلات" /><div style={{ fontSize: 12.6, lineHeight: 1.95, color: '#374151' }}>{p.education.map((e, i) => <div key={i}>— {e}</div>)}</div></div>}
        {(p.languages || []).length > 0 && <div><H t="اللغات" /><div style={{ fontSize: 12.6, lineHeight: 1.95, color: '#374151' }}>{p.languages.map((l, i) => <div key={i}>— {l}</div>)}</div></div>}
      </div>
    </div>
  );
};

const BODIES = { executive: Executive, sidebar: Sidebar, minimal: Minimal };

/* صفحة A4 واحدة بقياس ثابت — يُصغَّر المحتوى تلقائياً ليبقى في صفحة واحدة دائماً */
export const CvDocument = React.forwardRef(({ template = 'executive', accent = 'teal', profile, experiences = [], certifications = [], options = {} }, ref) => {
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const acc = ACCENTS.find((a) => a.key === accent) || ACCENTS[0];
  const Body = BODIES[template] || Executive;
  const opt = { contact: true, ...options };

  const exps = [...experiences].sort((a, b) => String(b.start_date || '').localeCompare(String(a.start_date || '')));
  const certs = [...certifications].sort((a, b) => String(b.issue_date || '').localeCompare(String(a.issue_date || '')));

  // ملاءمة تلقائية لصفحة واحدة: نوسّع عرض المحتوى ثم نصغّره بنفس النسبة — فيملأ عرض الصفحة كاملاً ويبقى في صفحة واحدة
  useLayoutEffect(() => {
    let cancelled = false;
    const fit = () => {
      const el = innerRef.current;
      if (!el || cancelled) return;
      let s = 1;
      for (let i = 0; i < 7; i++) {
        el.style.transform = 'none';
        el.style.width = `${A4.w / s}px`;
        const h = el.scrollHeight;
        const next = Math.max(0.6, Math.min(1.15, (A4.h - 26) / h));
        if (Math.abs(next - s) < 0.004) { s = next; break; }
        s = next;
      }
      el.style.width = `${A4.w / s}px`;
      el.style.transform = `scale(${s})`;
      setScale(s);
    };
    fit();
    const t = setTimeout(fit, 400);
    if (document.fonts?.ready) document.fonts.ready.then(fit).catch(() => {});
    return () => { cancelled = true; clearTimeout(t); };
  }, [template, accent, profile, experiences, certifications, JSON.stringify(opt)]);

  if (!profile) return null;

  const bandW = template === 'sidebar' ? Math.round(252 * scale) : 0;

  return (
    <div
      ref={ref}
      dir="rtl"
      data-testid="cv-document"
      data-band-color={bandW ? acc.d : ''}
      data-band-w={bandW || ''}
      style={{ width: A4.w, height: A4.h, background: '#fff', overflow: 'hidden', position: 'relative', boxSizing: 'border-box' }}
    >
      {bandW > 0 && <span style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: bandW, background: acc.d }} />}
      <div ref={innerRef} data-cv-inner="1" style={{ transformOrigin: 'top right', width: A4.w, position: 'relative' }}>
        <Body p={profile} exps={exps} certs={certs} acc={acc} opt={opt} />
      </div>
      {opt.brand !== false && (
        <div data-cv-brand="1" style={{ position: 'absolute', bottom: 7, left: 0, right: bandW, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_B, fontSize: 9.9, color: '#9aa4b2', background: 'transparent' }}>
          أُنشئت هذه السيرة عبر منصة تسيار · tasyar.com
        </div>
      )}
    </div>
  );
});

export default CvDocument;
